import { useState, useCallback, useRef } from 'react';
import { ARRoomScan, ARScanState, Wall, Point, FloorPlanElement, Room, ARWallMeasurement, ARScanPoint, Point3D, WallType } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';
import { calculateArea } from '../utils/floorPlanUtils';

interface UseARScanningProps {
  onWallsGenerated: (walls: Wall[], scanData: ARRoomScan) => void;
  currentFloor: number;
  shouldAlignWallsTo90Degrees?: boolean; // Whether to align walls to 90-degree angles
}

export const useARScanning = ({ onWallsGenerated, currentFloor, shouldAlignWallsTo90Degrees = true }: UseARScanningProps) => {
  const [scannedRooms, setScannedRooms] = useState<ARRoomScan[]>([]);
  const [currentScan, setCurrentScan] = useState<ARRoomScan | null>(null);
  const [isARScannerOpen, setIsARScannerOpen] = useState(false);
  const [isNativeARScannerOpen, setIsNativeARScannerOpen] = useState(false);
  
  // Reference to store the most recent scan for integration
  const lastScanRef = useRef<ARRoomScan | null>(null);

  // Convert AR scan coordinates to floor plan coordinates
  const convertARToFloorPlan = useCallback((scan: ARRoomScan): Wall[] => {
    console.log('[AR Conversion] Converting AR scan to floor plan:', {
      scanId: scan.id,
      wallsCount: scan.walls.length,
      walls: scan.walls
    });
    
    // Debug: Log the actual structure of the first wall
    if (scan.walls.length > 0) {
      console.log('[AR Conversion] First wall structure:', scan.walls[0]);
      console.log('[AR Conversion] First wall startPoint:', scan.walls[0].startPoint);
      console.log('[AR Conversion] First wall endPoint:', scan.walls[0].endPoint);
    }
    
    const walls: Wall[] = [];
    
    // Check if this is a native ARKit scan based on the data structure
    // Native AR scans might have different property structures
    let isNativeARKit = false;
    
    if (scan.walls.length > 0) {
      const firstWall = scan.walls[0] as any;
      
      // Check various possible data structures for native AR scans
      isNativeARKit = 
        // Check for direct start/end Point3D structure (native plugin format)
        (firstWall.start?.x !== undefined && firstWall.start?.z !== undefined &&
         firstWall.end?.x !== undefined && firstWall.end?.z !== undefined) ||
        // Check for worldCoordinate structure (legacy)
        (firstWall.startPoint?.worldCoordinate?.x !== undefined && 
         firstWall.startPoint?.worldCoordinate?.z !== undefined) ||
        // Check for direct startPoint coordinate structure
        (firstWall.startPoint?.x !== undefined && 
         firstWall.startPoint?.z !== undefined) ||
        // Check for transform structure
        (firstWall.transform !== undefined) ||
        // Check for position structure
        (firstWall.startPosition !== undefined && firstWall.endPosition !== undefined);
        
      console.log('[AR Conversion] Native ARKit scan detected:', isNativeARKit, {
        hasStartEnd: !!(firstWall.start && firstWall.end),
        startStructure: firstWall.start,
        endStructure: firstWall.end
      });
    }
    
    if (isNativeARKit) {
      console.log('[AR Conversion] Processing native ARKit scan with 3D coordinates');
      return convertNativeARKitToFloorPlan(scan);
    } else {
      console.log('[AR Conversion] Processing manual scan with estimated coordinates');
      return convertManualScanToFloorPlan(scan);
    }
  }, [currentFloor, shouldAlignWallsTo90Degrees]);

  // Align walls to canvas axes with confidence-based adjustment
  const alignWallsToAxes = (walls: Wall[], scale: number, isARScan: boolean = false): Wall[] => {
    if (walls.length === 0) return walls;
    
    console.log(`[Wall Alignment] 🔧 Applying 90-degree wall alignment... (AR scan: ${isARScan ? 'YES - minimal processing' : 'NO - full corrections'})`);
    
    // Step 1: Calculate confidence scores for each wall
    const wallsWithConfidence = walls.map((wall, idx) => {
      const length = Math.sqrt(
        Math.pow(wall.end.x - wall.start.x, 2) + 
        Math.pow(wall.end.y - wall.start.y, 2)
      );
      
      const lengthConfidence = Math.min(length / (5 * scale), 1);
      const arConfidence = (wall as any).arConfidence || 0.8;
      const totalConfidence = (lengthConfidence * 0.6) + (arConfidence * 0.4);
      
      return {
        wall,
        length,
        confidence: totalConfidence,
        originalIndex: idx
      };
    });
    
    // Step 2: Find the most confident wall for initial rotation
    const sortedWalls = [...wallsWithConfidence].sort((a, b) => b.confidence - a.confidence);
    const referenceWall = sortedWalls[0];
    
    console.log('[Wall Alignment] Walls by confidence:', sortedWalls.map(w => ({
      length: (w.length / scale).toFixed(2) + 'm',
      confidence: (w.confidence * 100).toFixed(0) + '%'
    })));
    
    // Step 3: Rotate all walls to align reference wall to nearest 90° axis
    const dx = referenceWall.wall.end.x - referenceWall.wall.start.x;
    const dy = referenceWall.wall.end.y - referenceWall.wall.start.y;
    const wallAngle = Math.atan2(dy, dx);
    const angleDegrees = (wallAngle * 180 / Math.PI);
    const nearestAxis = Math.round(angleDegrees / 90) * 90;
    const rotationAngle = (nearestAxis - angleDegrees) * Math.PI / 180;
    
    console.log('[Wall Alignment] Initial rotation:', {
      referenceAngle: angleDegrees.toFixed(1) + '°',
      targetAxis: nearestAxis + '°',
      rotation: ((nearestAxis - angleDegrees)).toFixed(1) + '°'
    });
    
    // Find rotation center
    let sumX = 0, sumY = 0, pointCount = 0;
    walls.forEach(wall => {
      sumX += wall.start.x + wall.end.x;
      sumY += wall.start.y + wall.end.y;
      pointCount += 2;
    });
    const centerX = sumX / pointCount;
    const centerY = sumY / pointCount;
    
    // Rotate all walls
    const cosAngle = Math.cos(rotationAngle);
    const sinAngle = Math.sin(rotationAngle);
    
    const rotatePoint = (point: Point): Point => {
      const x = point.x - centerX;
      const y = point.y - centerY;
      const rotatedX = x * cosAngle - y * sinAngle;
      const rotatedY = x * sinAngle + y * cosAngle;
      return {
        x: rotatedX + centerX,
        y: rotatedY + centerY
      };
    };
    
    let rotatedWalls = walls.map(wall => ({
      ...wall,
      start: rotatePoint(wall.start),
      end: rotatePoint(wall.end)
    }));
    
    // Step 4: CRITICAL FIX - Snap EACH wall to nearest cardinal direction (0°, 90°, 180°, 270°)
    console.log(`[Wall Alignment] 📐 Snapping each wall to cardinal axes... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
    
    let cardinalWalls: Wall[];
    
    // 🔧 AR SCAN FIX: Skip cardinal snapping for AR scans
    // ARKit already provides near-perfect orthogonal walls (<1° deviation)
    // Snapping breaks wall connectivity by moving endpoints independently
    if (isARScan) {
      console.log('[Wall Alignment]   ⏭️ Skipping cardinal snapping - AR walls already orthogonal, preserving connectivity');
      cardinalWalls = rotatedWalls;
    } else {
      // Manual drawings need cardinal snapping
      cardinalWalls = rotatedWalls.map((wall, idx) => {
        const dx = wall.end.x - wall.start.x;
        const dy = wall.end.y - wall.start.y;
        const angle = Math.atan2(dy, dx);
        const angleDeg = (angle * 180 / Math.PI);
        const length = Math.hypot(dx, dy);
        
        // Determine if wall should be horizontal (0°/180°) or vertical (90°/270°)
        const absAngle = Math.abs(angleDeg);
        const isHorizontal = absAngle < 45 || absAngle > 135;
        
        let snappedEnd: Point;
        let targetAngle: number;
        
        if (isHorizontal) {
          // Snap to horizontal (keep Y constant, adjust X for length)
          snappedEnd = {
            x: wall.start.x + (dx > 0 ? length : -length),
            y: wall.start.y
          };
          targetAngle = dx > 0 ? 0 : 180;
          console.log(`[Wall Alignment]   Wall ${idx}: ${angleDeg.toFixed(1)}° → ${targetAngle}° (horizontal)`);
        } else {
          // Snap to vertical (keep X constant, adjust Y for length)
          snappedEnd = {
            x: wall.start.x,
            y: wall.start.y + (dy > 0 ? length : -length)
          };
          targetAngle = dy > 0 ? 90 : 270;
          console.log(`[Wall Alignment]   Wall ${idx}: ${angleDeg.toFixed(1)}° → ${targetAngle}° (vertical)`);
        }
        
        return {
          ...wall,
          end: snappedEnd
        };
      });
    }
    
    // Step 5: Build connectivity graph
    const CORNER_TOLERANCE = 60; // pixels - increased tolerance for AR scan precision and multi-room connection
    
    interface Corner {
      x: number;
      y: number;
      wallIndices: Array<{ wallIdx: number; isStart: boolean }>;
    }
    
    const corners: Corner[] = [];
    
    // Find all unique corners
    cardinalWalls.forEach((wall, idx) => {
      [wall.start, wall.end].forEach((point, pointIdx) => {
        const isStart = pointIdx === 0;
        
        // Find existing corner
        let corner = corners.find(c => 
          Math.hypot(c.x - point.x, c.y - point.y) < CORNER_TOLERANCE
        );
        
        if (!corner) {
          corner = { x: point.x, y: point.y, wallIndices: [] };
          corners.push(corner);
        }
        
        corner.wallIndices.push({ wallIdx: idx, isStart });
      });
    });
    
    console.log(`[Wall Alignment] 🔗 Found ${corners.length} corners`);
    
    // Step 6: Find and fix all corner intersections
    // For orthogonal walls, we need to find where they SHOULD intersect
    console.log(`[Wall Alignment] 🔗 Finding intersection points for connected walls... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
    
    let adjustedWalls: Wall[];
    
    // 🔧 AR SCAN FIX: Skip corner intersection adjustment for AR scans
    // ARKit provides accurate corner positions, forcing intersections breaks wall continuity
    if (isARScan) {
      console.log('[Wall Alignment]   ⏭️ Skipping corner intersection adjustment - AR scan corners are already accurate');
      adjustedWalls = cardinalWalls; // Use walls as-is after cardinal snapping
    } else {
      // Manual drawings need corner intersection fixes
      adjustedWalls = [...cardinalWalls];
      const processed = new Set<string>();
      
      // Helper: Calculate intersection point for two orthogonal walls
      const findIntersection = (wall1: Wall, wall2: Wall): Point | null => {
        const w1_isHorizontal = Math.abs(wall1.end.x - wall1.start.x) > Math.abs(wall1.end.y - wall1.start.y);
        const w2_isHorizontal = Math.abs(wall2.end.x - wall2.start.x) > Math.abs(wall2.end.y - wall2.start.y);
        
        // Walls must be perpendicular for intersection
        if (w1_isHorizontal === w2_isHorizontal) return null;
        
        if (w1_isHorizontal) {
          // Wall1 is horizontal, Wall2 is vertical
          return {
            x: wall2.start.x, // Use vertical wall's X
            y: wall1.start.y  // Use horizontal wall's Y
          };
        } else {
          // Wall1 is vertical, Wall2 is horizontal
          return {
            x: wall1.start.x, // Use vertical wall's X
            y: wall2.start.y  // Use horizontal wall's Y
          };
        }
      };
      
      // Process each corner to find proper intersections
      corners.forEach((corner, cornerIndex) => {
        if (corner.wallIndices.length < 2) return; // Skip corners with less than 2 walls
        
        // 🔧 MULTI-ROOM FIX: Process corners with 2+ walls (including shared corners between rooms)
        if (corner.wallIndices.length > 2) {
          console.log(`[Wall Alignment]   🔗 Multi-room corner ${cornerIndex}: ${corner.wallIndices.length} walls converging at (${corner.x.toFixed(0)}, ${corner.y.toFixed(0)})`);
        }
        
        // 🔧 MULTI-ROOM FIX: Process all wall pairs at this corner
        for (let i = 0; i < corner.wallIndices.length; i++) {
          for (let j = i + 1; j < corner.wallIndices.length; j++) {
            const wall1Idx = corner.wallIndices[i].wallIdx;
            const wall2Idx = corner.wallIndices[j].wallIdx;
            const wall1IsStart = corner.wallIndices[i].isStart;
            const wall2IsStart = corner.wallIndices[j].isStart;
            
            const pairKey = [wall1Idx, wall2Idx].sort().join('-');
            if (processed.has(pairKey)) continue;
            processed.add(pairKey);
            
            const wall1 = adjustedWalls[wall1Idx];
            const wall2 = adjustedWalls[wall2Idx];
            
            // Find intersection point
            const intersection = findIntersection(wall1, wall2);
            if (!intersection) continue;
            
            const conf1 = wallsWithConfidence[wall1Idx].confidence;
            const conf2 = wallsWithConfidence[wall2Idx].confidence;
            
            console.log(`[Wall Alignment]   Corner ${cornerIndex}: Walls ${wall1Idx} & ${wall2Idx} → intersection at (${intersection.x.toFixed(0)}, ${intersection.y.toFixed(0)})`);
            
            // Adjust both walls to meet at intersection
            // Higher confidence wall gets priority, but both endpoints move
            if (conf1 >= conf2) {
              // Adjust wall1's endpoint, then wall2's endpoint
              if (wall1IsStart) {
                adjustedWalls[wall1Idx] = {
                  ...wall1,
                  start: intersection
                };
              } else {
                adjustedWalls[wall1Idx] = {
                  ...wall1,
                  end: intersection
                };
              }
              
              if (wall2IsStart) {
                adjustedWalls[wall2Idx] = {
                  ...wall2,
                  start: intersection
                };
              } else {
                adjustedWalls[wall2Idx] = {
                  ...wall2,
                  end: intersection
                };
              }
            } else {
              // Adjust wall2's endpoint first, then wall1's
              if (wall2IsStart) {
                adjustedWalls[wall2Idx] = {
                  ...wall2,
                  start: intersection
                };
              } else {
                adjustedWalls[wall2Idx] = {
                  ...wall2,
                  end: intersection
                };
              }
              
              if (wall1IsStart) {
                adjustedWalls[wall1Idx] = {
                  ...wall1,
                  start: intersection
                };
              } else {
                adjustedWalls[wall1Idx] = {
                  ...wall1,
                  end: intersection
                };
              }
            }
          }
        }
      });
    }
    
    // Step 6.5: T-JUNCTION WALL SPLITTING - Handle hallways connecting to room middles
    console.log(`[Wall Alignment] 🔀 Detecting T-junctions (endpoints touching wall middles)... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
    
    // 🔧 AR SCAN FIX: Skip T-junction splitting for AR scans - ARKit provides accurate connectivity
    if (isARScan) {
      console.log('[Wall Alignment]   ⏭️ Skipping T-junction splitting - AR scan walls are already accurate');
      // Use original walls without splitting
      const wallsAfterSplitting = adjustedWalls;
      const wallsWithConfidenceAfterSplitting = wallsWithConfidence;
      
      // Skip to wall trimming step (which also skips for AR scans)
      console.log(`[Wall Alignment] 🔍 Checking for gaps and overextensions... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
      
      // For AR scans, skip trimming and return adjusted walls
      console.log(`[Wall Alignment] ⚖️ Equalizing opposite wall lengths based on confidence... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
      console.log('[Wall Alignment]   ⏭️ Skipping wall equalization - AR scan data is already accurate');
      return wallsAfterSplitting;
    }
    
    const T_JUNCTION_TOLERANCE = 30; // pixels - how close an endpoint must be to split a wall
    const wallsWithSplits: Wall[] = [];
    const wallsToSplit = new Set<number>();
    
    // Track split points for each wall
    const splitPoints = new Map<number, Point[]>();
    
    adjustedWalls.forEach((wall, wallIdx) => {
      adjustedWalls.forEach((otherWall, otherIdx) => {
        if (wallIdx === otherIdx) return;
        
        // Check if either endpoint of otherWall is near the middle of wall
        [otherWall.start, otherWall.end].forEach((point, pointIdx) => {
          // Calculate distance from point to wall segment
          const dx = wall.end.x - wall.start.x;
          const dy = wall.end.y - wall.start.y;
          const lengthSquared = dx * dx + dy * dy;
          if (lengthSquared === 0) return; // Degenerate wall
          
          // Project point onto wall line
          const t = Math.max(0, Math.min(1, 
            ((point.x - wall.start.x) * dx + (point.y - wall.start.y) * dy) / lengthSquared
          ));
          
          const projX = wall.start.x + t * dx;
          const projY = wall.start.y + t * dy;
          
          // Distance from point to projected point on wall
          const distance = Math.hypot(point.x - projX, point.y - projY);
          
          // Is this point very close to the wall?
          if (distance < T_JUNCTION_TOLERANCE) {
            // Is it NOT at the endpoints (i.e., in the middle)?
            const distToStart = Math.hypot(point.x - wall.start.x, point.y - wall.start.y);
            const distToEnd = Math.hypot(point.x - wall.end.x, point.y - wall.end.y);
            
            // Only split if it's in the middle (not near endpoints)
            if (distToStart > CORNER_TOLERANCE && distToEnd > CORNER_TOLERANCE && t > 0.1 && t < 0.9) {
              console.log(`[Wall Alignment]   🔀 T-junction detected: Wall ${otherIdx} ${pointIdx === 0 ? 'start' : 'end'} touches middle of wall ${wallIdx} at t=${t.toFixed(2)} (dist=${distance.toFixed(1)}px)`);
              
              if (!splitPoints.has(wallIdx)) {
                splitPoints.set(wallIdx, []);
              }
              splitPoints.get(wallIdx)!.push({ x: projX, y: projY });
              wallsToSplit.add(wallIdx);
            }
          }
        });
      });
    });
    
    console.log(`[Wall Alignment] 🔀 Found ${wallsToSplit.size} walls to split at T-junctions`);
    
    // Split walls and rebuild array
    const finalAdjustedWalls: Wall[] = [];
    adjustedWalls.forEach((wall, idx) => {
      if (wallsToSplit.has(idx)) {
        const splits = splitPoints.get(idx)!;
        
        // Sort split points along the wall
        const dx = wall.end.x - wall.start.x;
        const dy = wall.end.y - wall.start.y;
        const sortedSplits = splits.sort((a, b) => {
          const tA = ((a.x - wall.start.x) * dx + (a.y - wall.start.y) * dy) / (dx * dx + dy * dy);
          const tB = ((b.x - wall.start.x) * dx + (b.y - wall.start.y) * dy) / (dx * dx + dy * dy);
          return tA - tB;
        });
        
        console.log(`[Wall Alignment]   ✂️ Splitting wall ${idx} into ${sortedSplits.length + 1} segments`);
        
        // Create segments
        let currentStart = wall.start;
        sortedSplits.forEach((splitPoint, splitIdx) => {
          finalAdjustedWalls.push({
            ...wall,
            start: currentStart,
            end: splitPoint
          });
          currentStart = splitPoint;
        });
        
        // Add final segment
        finalAdjustedWalls.push({
          ...wall,
          start: currentStart,
          end: wall.end
        });
      } else {
        finalAdjustedWalls.push(wall);
      }
    });
    
    console.log(`[Wall Alignment] 🔀 Wall count after T-junction splitting: ${adjustedWalls.length} → ${finalAdjustedWalls.length}`);
    
    // 🔧 CRITICAL: Expand wallsWithConfidence array to match split walls
    const expandedWallsWithConfidence: Array<{ wall: Wall; confidence: number }> = [];
    let splitWallIndex = 0;
    adjustedWalls.forEach((originalWall, originalIdx) => {
      if (wallsToSplit.has(originalIdx)) {
        const splits = splitPoints.get(originalIdx)!;
        const segmentCount = splits.length + 1;
        const originalConfidence = wallsWithConfidence[originalIdx].confidence;
        
        // Add confidence for each segment (inherit from original wall)
        for (let i = 0; i < segmentCount; i++) {
          expandedWallsWithConfidence.push({
            wall: finalAdjustedWalls[splitWallIndex],
            confidence: originalConfidence
          });
          splitWallIndex++;
        }
      } else {
        expandedWallsWithConfidence.push(wallsWithConfidence[originalIdx]);
        splitWallIndex++;
      }
    });
    
    console.log(`[Wall Alignment] 🔀 Confidence array expanded: ${wallsWithConfidence.length} → ${expandedWallsWithConfidence.length}`);
    
    // Replace adjustedWalls with split walls
    const wallsAfterSplitting = finalAdjustedWalls;
    const wallsWithConfidenceAfterSplitting = expandedWallsWithConfidence;
    
    // Step 7: Extend short low-confidence walls & Trim overextended walls
    console.log('[Wall Alignment] 📏 Analyzing wall lengths against room geometry...');
    
    // Log current state with confidence
    adjustedWalls.forEach((wall, idx) => {
      const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      const confidence = wallsWithConfidence[idx].confidence;
      const arConf = (walls[idx] as any).arConfidence || 0.8;
      console.log(`[Wall Alignment]   Wall ${idx}: ${(wallLength / scale).toFixed(2)}m, confidence=${(confidence * 100).toFixed(0)}%, AR=${(arConf * 100).toFixed(0)}%`);
    });
    
    console.log(`[Wall Alignment] 🔍 Checking for gaps and overextensions... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
    
    const adjustedAndTrimmedWalls = wallsAfterSplitting.map((wall, idx) => {
      const isHorizontal = Math.abs(wall.end.x - wall.start.x) > Math.abs(wall.end.y - wall.start.y);
      let adjustedStart = { ...wall.start };
      let adjustedEnd = { ...wall.end };
      let wasAdjusted = false;
      const wallConfidence = wallsWithConfidenceAfterSplitting[idx].confidence;
      
      // 🔧 AR SCAN FIX: Skip trimming for AR scans - LiDAR data is already accurate
      if (isARScan) {
        return wall;
      }
      const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      
      // For low-confidence walls, check if they need to be extended
      const isLowConfidence = wallConfidence < 0.6; // Below 60% confidence
      if (isLowConfidence) {
        console.log(`[Wall Alignment]   🔍 Wall ${idx} has LOW confidence (${(wallConfidence * 100).toFixed(0)}%), checking for gaps...`);
      }
      
      // Check against all other walls for potential trimming points
      adjustedWalls.forEach((otherWall, otherIdx) => {
        if (idx === otherIdx) return;
        
        const otherIsHorizontal = Math.abs(otherWall.end.x - otherWall.start.x) > Math.abs(otherWall.end.y - otherWall.start.y);
        
        // Only check perpendicular walls
        if (isHorizontal === otherIsHorizontal) return;
        
        if (isHorizontal) {
          // This wall is horizontal, check if it should be trimmed by vertical walls
          const thisY = wall.start.y;
          const otherX = otherWall.start.x;
          const otherYMin = Math.min(otherWall.start.y, otherWall.end.y);
          const otherYMax = Math.max(otherWall.start.y, otherWall.end.y);
          
          // Check if vertical wall crosses this horizontal wall
          if (thisY >= otherYMin && thisY <= otherYMax) {
            // Check if we need to trim the start
            const startX = Math.min(wall.start.x, wall.end.x);
            const endX = Math.max(wall.start.x, wall.end.x);
            
            if (otherX > startX && otherX < endX) {
              // Vertical wall is between our endpoints - might need trimming
              const distToStart = Math.abs(otherX - wall.start.x);
              const distToEnd = Math.abs(otherX - wall.end.x);
              
              // For low-confidence walls, extend to meet perpendicular walls
              // For high-confidence walls, trim if too close
              if (isLowConfidence) {
                // Check if there's a gap
                if (distToStart > 10 && distToStart < 50) {
                  console.log(`[Wall Alignment]     ⬆️ Extending wall ${idx} start to meet perpendicular wall (gap: ${distToStart.toFixed(0)}px)`);
                  adjustedStart.x = otherX;
                  wasAdjusted = true;
                } else if (distToEnd > 10 && distToEnd < 50) {
                  console.log(`[Wall Alignment]     ⬆️ Extending wall ${idx} end to meet perpendicular wall (gap: ${distToEnd.toFixed(0)}px)`);
                  adjustedEnd.x = otherX;
                  wasAdjusted = true;
                }
              } else {
                // High confidence: trim if overextending
                if (distToStart < 5) {
                  adjustedStart.x = otherX;
                  wasAdjusted = true;
                } else if (distToEnd < 5) {
                  adjustedEnd.x = otherX;
                  wasAdjusted = true;
                }
              }
            }
          }
        } else {
          // This wall is vertical, check if it should be trimmed by horizontal walls
          const thisX = wall.start.x;
          const otherY = otherWall.start.y;
          const otherXMin = Math.min(otherWall.start.x, otherWall.end.x);
          const otherXMax = Math.max(otherWall.start.x, otherWall.end.x);
          
          // Check if horizontal wall could trim this vertical wall
          // The vertical wall's X must be within the horizontal wall's span
          if (thisX >= otherXMin - 5 && thisX <= otherXMax + 5) {
            // Check if either endpoint of vertical wall extends beyond the horizontal wall
            const tolerance = 20; // pixels
            
            // Check if start point needs adjustment
            const startDistToOther = Math.abs(wall.start.y - otherY);
            if (startDistToOther < tolerance && startDistToOther > 1) {
              const endDistToOther = Math.abs(wall.end.y - otherY);
              
              // For low-confidence walls, extend to fill gaps
              // For high-confidence walls, trim if overextending
              if (isLowConfidence) {
                // Check if there's a gap
                if (startDistToOther > 10 && startDistToOther < 50 && endDistToOther > startDistToOther + 10) {
                  console.log(`[Wall Alignment]     ⬆️ Extending wall ${idx} start to meet perpendicular wall (gap: ${startDistToOther.toFixed(0)}px)`);
                  adjustedStart.y = otherY;
                  wasAdjusted = true;
                }
              } else {
                // High confidence: trim if overextending
                if (endDistToOther > startDistToOther + 10 && startDistToOther < 10) {
                  adjustedStart.y = otherY;
                  wasAdjusted = true;
                  console.log(`[Wall Alignment]     ✂️ Wall ${idx} start trimmed at Y=${otherY}`);
                }
              }
            }
            
            // Check if end point needs adjustment
            const endDistToOther = Math.abs(wall.end.y - otherY);
            if (endDistToOther < tolerance && endDistToOther > 1) {
              const startDistToOther = Math.abs(wall.start.y - otherY);
              
              // For low-confidence walls, extend to fill gaps
              // For high-confidence walls, trim if overextending
              if (isLowConfidence) {
                // Check if there's a gap
                if (endDistToOther > 10 && endDistToOther < 50 && startDistToOther > endDistToOther + 10) {
                  console.log(`[Wall Alignment]     ⬆️ Extending wall ${idx} end to meet perpendicular wall (gap: ${endDistToOther.toFixed(0)}px)`);
                  adjustedEnd.y = otherY;
                  wasAdjusted = true;
                }
              } else {
                // High confidence: trim if overextending
                if (startDistToOther > endDistToOther + 10 && endDistToOther < 10) {
                  adjustedEnd.y = otherY;
                  wasAdjusted = true;
                  console.log(`[Wall Alignment]     ✂️ Wall ${idx} end trimmed at Y=${otherY}`);
                }
              }
            }
          }
        }
      });
      
      if (wasAdjusted) {
        const newLength = Math.hypot(adjustedEnd.x - adjustedStart.x, adjustedEnd.y - adjustedStart.y);
        const lengthChange = newLength - wallLength;
        const action = lengthChange > 0 ? 'Extended' : 'Trimmed';
        console.log(`[Wall Alignment]   ${lengthChange > 0 ? '📐' : '✂️'} ${action} wall ${idx}: ${(wallLength / scale).toFixed(2)}m → ${(newLength / scale).toFixed(2)}m (${lengthChange > 0 ? '+' : ''}${(lengthChange / scale).toFixed(2)}m)`);
      }
      
      return {
        ...wall,
        start: adjustedStart,
        end: adjustedEnd
      };
    });
    
    // Step 8: Equalize opposite walls based on confidence
    console.log(`[Wall Alignment] ⚖️ Equalizing opposite wall lengths based on confidence... (${isARScan ? 'SKIPPED for AR scans' : 'ENABLED'})`);
    
    // 🔧 AR SCAN FIX: Skip equalization for AR scans - LiDAR measurements are already accurate
    if (isARScan) {
      console.log('[Wall Alignment]   ⏭️ Skipping wall equalization - AR scan data is already accurate');
      return adjustedAndTrimmedWalls;
    }
    
    // Note: Skip equalization for now since T-junction splitting may have created precise wall segments
    const equalizedWalls = [...adjustedAndTrimmedWalls];
    const processedPairs = new Set<number>();
    let candidatesChecked = 0;
    
    // Find opposite walls (parallel walls facing each other)
    for (let i = 0; i < equalizedWalls.length; i++) {
      if (processedPairs.has(i)) continue;
      
      const wall1 = equalizedWalls[i];
      const wall1Length = Math.hypot(wall1.end.x - wall1.start.x, wall1.end.y - wall1.start.y);
      const wall1IsHorizontal = Math.abs(wall1.end.x - wall1.start.x) > Math.abs(wall1.end.y - wall1.start.y);
      const wall1Conf = wallsWithConfidenceAfterSplitting[i].confidence;
      
      // Find potential opposite walls (same orientation)
      for (let j = i + 1; j < equalizedWalls.length; j++) {
        if (processedPairs.has(j)) continue;
        
        const wall2 = equalizedWalls[j];
        const wall2Length = Math.hypot(wall2.end.x - wall2.start.x, wall2.end.y - wall2.start.y);
        const wall2IsHorizontal = Math.abs(wall2.end.x - wall2.start.x) > Math.abs(wall2.end.y - wall2.start.y);
        const wall2Conf = wallsWithConfidenceAfterSplitting[j].confidence;
        
        // Must be same orientation
        if (wall1IsHorizontal !== wall2IsHorizontal) continue;
        
        // Check if they're parallel and facing each other
        const lengthDiff = Math.abs(wall1Length - wall2Length);
        const avgLength = (wall1Length + wall2Length) / 2;
        const lengthDiffPercent = (lengthDiff / avgLength) * 100;
        const confDiff = Math.abs(wall1Conf - wall2Conf);
        
        candidatesChecked++;
        // Skip logging for split walls to reduce noise
        // console.log(`[Wall Alignment]   🔍 Checking walls ${i} & ${j}: ${(wall1Length / scale).toFixed(2)}m (${(wall1Conf * 100).toFixed(0)}%) vs ${(wall2Length / scale).toFixed(2)}m (${(wall2Conf * 100).toFixed(0)}%) → length diff ${lengthDiffPercent.toFixed(1)}%, conf diff ${(confDiff * 100).toFixed(0)}%`);
        
        // If lengths are within 15% of each other, they might be opposite walls
        if (lengthDiffPercent < 15) {
          // Check confidence difference
          
          // If confidence differs by more than 15%, adjust the lower confidence wall
          if (confDiff > 0.15) {
            const [lowConfIdx, highConfIdx] = wall1Conf < wall2Conf ? [i, j] : [j, i];
            const lowConfWall = equalizedWalls[lowConfIdx];
            const highConfWall = equalizedWalls[highConfIdx];
            const targetLength = Math.hypot(
              highConfWall.end.x - highConfWall.start.x,
              highConfWall.end.y - highConfWall.start.y
            );
            const currentLength = Math.hypot(
              lowConfWall.end.x - lowConfWall.start.x,
              lowConfWall.end.y - lowConfWall.start.y
            );
            
            // Scale the low confidence wall to match high confidence wall length
            const scaleFactor = targetLength / currentLength;
            const dx = lowConfWall.end.x - lowConfWall.start.x;
            const dy = lowConfWall.end.y - lowConfWall.start.y;
            
            console.log(`[Wall Alignment]   ⚖️ Equalizing opposite walls ${lowConfIdx} & ${highConfIdx}:`);
            console.log(`[Wall Alignment]     High-conf wall ${highConfIdx}: ${(targetLength / scale).toFixed(2)}m (${(wallsWithConfidenceAfterSplitting[highConfIdx].confidence * 100).toFixed(0)}%)`);
            console.log(`[Wall Alignment]     Low-conf wall ${lowConfIdx}: ${(currentLength / scale).toFixed(2)}m → ${(targetLength / scale).toFixed(2)}m (${(wallsWithConfidenceAfterSplitting[lowConfIdx].confidence * 100).toFixed(0)}%)`);
            
            equalizedWalls[lowConfIdx] = {
              ...lowConfWall,
              end: {
                x: lowConfWall.start.x + dx * scaleFactor,
                y: lowConfWall.start.y + dy * scaleFactor
              }
            };
            
            processedPairs.add(lowConfIdx);
            processedPairs.add(highConfIdx);
            break;
          }
        }
      }
    }
    
    if (processedPairs.size === 0) {
      console.log(`[Wall Alignment]   ℹ️ No opposite walls equalized (checked ${candidatesChecked} parallel pairs, none met both criteria: <15% length diff AND >15% confidence diff)`);
    } else {
      console.log(`[Wall Alignment]   ✅ Equalized ${processedPairs.size / 2} pairs of opposite walls out of ${candidatesChecked} candidates`);
    }
    
    // Step 9: Round final coordinates
    const finalWalls = equalizedWalls.map(wall => ({
      ...wall,
      start: { x: Math.round(wall.start.x), y: Math.round(wall.start.y) },
      end: { x: Math.round(wall.end.x), y: Math.round(wall.end.y) }
    }));
    
    console.log('[Wall Alignment] ✅ All walls snapped to perfect orthogonal angles with proper geometry');
    
    return finalWalls;
  };

  // Convert native ARKit scan with precise 3D coordinates
  const convertNativeARKitToFloorPlan = useCallback((scan: ARRoomScan): Wall[] => {
    console.log('[Native AR Conversion] Converting native ARKit scan:', scan);
    console.log('[Native AR Conversion] Enhanced processing:', (scan as any).enhancedProcessing ? 'ENABLED ✅' : 'DISABLED');
    console.log('[Native AR Conversion] Processing version:', (scan as any).processingVersion || 'phase1');
    
    // Phase 2 enhancements - check for corner and connectivity data
    const corners = (scan as any).corners || [];
    const connectivity = (scan as any).connectivity || {};
    const qualityMetrics = (scan as any).qualityMetrics || {};
    
    if (corners.length > 0) {
      console.log(`[Native AR Conversion] Phase 2: Detected ${corners.length} corners`);
      console.log(`[Native AR Conversion] Phase 2: Connectivity ${connectivity.connectivityRatio ? (connectivity.connectivityRatio * 100).toFixed(0) : 0}%`);
      console.log(`[Native AR Conversion] Phase 2: Room closure ${connectivity.isClosedRoom ? '✅ CLOSED' : '⚠️ OPEN'}`);
    }
    
    // Phase 3 enhancements - quality metrics
    if (qualityMetrics.overallQuality !== undefined) {
      console.log(`[Native AR Conversion] Phase 3: Overall quality ${(qualityMetrics.overallQuality * 100).toFixed(0)}% (${qualityMetrics.qualityLevel})`);
      console.log(`[Native AR Conversion] Phase 3: Stability ${(qualityMetrics.wallStability * 100).toFixed(0)}%, Coverage ${(qualityMetrics.coverageScore * 100).toFixed(0)}%`);
      console.log(`[Native AR Conversion] Phase 3: Tracked ${qualityMetrics.trackedWalls} walls over ${qualityMetrics.frameCount} frames`);
    }
    
    if (!scan.walls || scan.walls.length === 0) {
      console.log('[Native AR Conversion] No walls to convert');
      return [];
    }
    
    console.log('[Native AR Conversion] Processing walls to find room boundaries...');
    
    // Step 1: Extract and validate wall segments from ARKit
    const validWallSegments: Array<{
      start: { x: number; z: number };
      end: { x: number; z: number };
      length: number;
      confidence: number;
    }> = [];
    
    scan.walls.forEach((arWall: any, index) => {
      console.log(`[Native AR Conversion] Processing wall ${index}:`, arWall);
      
      let start3D: { x: number; z: number } | null = null;
      let end3D: { x: number; z: number } | null = null;
      
      // Try to extract coordinates from various possible structures
      try {
        // Method 1: worldCoordinate structure
        if (arWall.startPoint?.worldCoordinate && arWall.endPoint?.worldCoordinate) {
          start3D = { x: arWall.startPoint.worldCoordinate.x, z: arWall.startPoint.worldCoordinate.z };
          end3D = { x: arWall.endPoint.worldCoordinate.x, z: arWall.endPoint.worldCoordinate.z };
          console.log(`[Native AR Conversion] Wall ${index}: Using worldCoordinate structure`);
        }
        // Method 2: Direct coordinates on points
        else if (arWall.startPoint?.x !== undefined && arWall.startPoint?.z !== undefined &&
                 arWall.endPoint?.x !== undefined && arWall.endPoint?.z !== undefined) {
          start3D = { x: arWall.startPoint.x, z: arWall.startPoint.z };
          end3D = { x: arWall.endPoint.x, z: arWall.endPoint.z };
          console.log(`[Native AR Conversion] Wall ${index}: Using direct coordinate structure`);
        }
        // Method 3: Check if coordinates are on the wall object directly
        else if (arWall.start?.x !== undefined && arWall.start?.z !== undefined &&
                 arWall.end?.x !== undefined && arWall.end?.z !== undefined) {
          start3D = { x: arWall.start.x, z: arWall.start.z };
          end3D = { x: arWall.end.x, z: arWall.end.z };
          console.log(`[Native AR Conversion] Wall ${index}: Using wall direct coordinate structure`);
        }
        // Method 4: Try array-based coordinates
        else if (Array.isArray(arWall.coordinates) && arWall.coordinates.length >= 2) {
          start3D = { x: arWall.coordinates[0].x || arWall.coordinates[0][0], z: arWall.coordinates[0].z || arWall.coordinates[0][2] };
          end3D = { x: arWall.coordinates[1].x || arWall.coordinates[1][0], z: arWall.coordinates[1].z || arWall.coordinates[1][2] };
          console.log(`[Native AR Conversion] Wall ${index}: Using coordinate array structure`);
        }
        
        if (start3D && end3D) {
          // Calculate 3D wall length for validation
          const wallLength3D = Math.sqrt(
            Math.pow(end3D.x - start3D.x, 2) + 
            Math.pow(end3D.z - start3D.z, 2)
          );
          
          // Filter out walls that are too short (likely furniture) or too long (likely errors)
          if (wallLength3D >= 0.3 && wallLength3D <= 20) { // Between 30cm and 20m
            validWallSegments.push({
              start: start3D,
              end: end3D,
              length: wallLength3D,
              confidence: arWall.confidence || 0.8
            });
            console.log(`[Native AR Conversion] Valid wall ${index}: ${wallLength3D.toFixed(2)}m (confidence: ${(arWall.confidence || 0.8).toFixed(2)})`);
          } else {
            console.log(`[Native AR Conversion] Filtered out wall ${index}: ${wallLength3D.toFixed(2)}m (too short/long)`);
          }
        } else {
          console.log(`[Native AR Conversion] Could not extract coordinates from wall ${index}`);
        }
      } catch (error) {
        console.error(`[Native AR Conversion] Error processing wall ${index}:`, error);
      }
    });
    
    if (validWallSegments.length === 0) {
      console.log('[Native AR Conversion] No valid wall segments found, creating fallback rectangular room');
      // Create a simple 4x4 meter room as fallback
      const roomSize = 4;
      const scale = PIXELS_PER_METER;
      const centerX = 1500;
      const centerY = 1500;
      const halfSize = (roomSize * scale) / 2;
      
      return [
        {
          id: `fallback-wall-1-${uuidv4()}`,
          start: { x: centerX - halfSize, y: centerY - halfSize },
          end: { x: centerX + halfSize, y: centerY - halfSize },
          type: 'None',
          thickness: 10,
          color: '#000000',
          floor: currentFloor,
          external: true,
          isTemporary: false
        },
        {
          id: `fallback-wall-2-${uuidv4()}`,
          start: { x: centerX + halfSize, y: centerY - halfSize },
          end: { x: centerX + halfSize, y: centerY + halfSize },
          type: 'None',
          thickness: 10,
          color: '#000000',
          floor: currentFloor,
          external: true,
          isTemporary: false
        },
        {
          id: `fallback-wall-3-${uuidv4()}`,
          start: { x: centerX + halfSize, y: centerY + halfSize },
          end: { x: centerX - halfSize, y: centerY + halfSize },
          type: 'None',
          thickness: 10,
          color: '#000000',
          floor: currentFloor,
          external: true,
          isTemporary: false
        },
        {
          id: `fallback-wall-4-${uuidv4()}`,
          start: { x: centerX - halfSize, y: centerY + halfSize },
          end: { x: centerX - halfSize, y: centerY - halfSize },
          type: 'None',
          thickness: 10,
          color: '#000000',
          floor: currentFloor,
          external: true,
          isTemporary: false
        }
      ];
    }

    console.log(`[Native AR Conversion] Found ${validWallSegments.length} valid wall segments`);
    
    // Step 2: Enhanced wall endpoint snapping
    const snapWallEndpoints = (segments: typeof validWallSegments) => {
      const snapThreshold = 0.15; // 15cm snap distance
      const snappedSegments = [...segments];
      
      // For each wall, check if endpoints are close to other wall endpoints
      for (let i = 0; i < snappedSegments.length; i++) {
        for (let j = i + 1; j < snappedSegments.length; j++) {
          const seg1 = snappedSegments[i];
          const seg2 = snappedSegments[j];
          
          // Check all endpoint combinations
          const distances = [
            { type: 'start-start', dist: Math.sqrt(Math.pow(seg1.start.x - seg2.start.x, 2) + Math.pow(seg1.start.z - seg2.start.z, 2)) },
            { type: 'start-end', dist: Math.sqrt(Math.pow(seg1.start.x - seg2.end.x, 2) + Math.pow(seg1.start.z - seg2.end.z, 2)) },
            { type: 'end-start', dist: Math.sqrt(Math.pow(seg1.end.x - seg2.start.x, 2) + Math.pow(seg1.end.z - seg2.start.z, 2)) },
            { type: 'end-end', dist: Math.sqrt(Math.pow(seg1.end.x - seg2.end.x, 2) + Math.pow(seg1.end.z - seg2.end.z, 2)) }
          ];
          
          // Find closest pair
          const closest = distances.reduce((min, curr) => curr.dist < min.dist ? curr : min);
          
          if (closest.dist < snapThreshold) {
            // Snap endpoints together (average position)
            const snapPoint = {
              x: closest.type.includes('start-start') || closest.type === 'start-end' 
                ? (seg1.start.x + (closest.type === 'start-start' ? seg2.start.x : seg2.end.x)) / 2
                : (seg1.end.x + (closest.type === 'end-start' ? seg2.start.x : seg2.end.x)) / 2,
              z: closest.type.includes('start-start') || closest.type === 'start-end'
                ? (seg1.start.z + (closest.type === 'start-start' ? seg2.start.z : seg2.end.z)) / 2
                : (seg1.end.z + (closest.type === 'end-start' ? seg2.start.z : seg2.end.z)) / 2
            };
            
            // Apply snap
            if (closest.type.startsWith('start-')) {
              seg1.start = snapPoint;
            } else {
              seg1.end = snapPoint;
            }
            
            if (closest.type.includes('-start')) {
              seg2.start = snapPoint;
            } else {
              seg2.end = snapPoint;
            }
            
            console.log(`[Native AR Conversion] Snapped ${closest.type} (distance: ${closest.dist.toFixed(3)}m)`);
          }
        }
      }
      
      return snappedSegments;
    };
    
    // Step 2.8: Apply corner-based snapping using detected corners
    const applyCornerSnapping = (segments: typeof validWallSegments, detectedCorners: any[]) => {
      console.log(`[Native AR Conversion] Applying corner-based snapping with ${detectedCorners.length} corners`);
      
      const snappedSegments = segments.map(seg => ({ ...seg })); // Clone segments
      
      detectedCorners.forEach((corner: any) => {
        const cornerPos = corner.position;
        const cornerX = cornerPos.x;
        const cornerZ = cornerPos.z;
        const confidence = corner.confidence || 0.8;
        
        // Only use high-confidence corners
        if (confidence < 0.7) return;
        
        // Snap wall endpoints to this corner if they're close
        const snapRadius = 0.10; // 10cm radius for corner snapping
        
        snappedSegments.forEach(seg => {
          const startDist = Math.sqrt(Math.pow(seg.start.x - cornerX, 2) + Math.pow(seg.start.z - cornerZ, 2));
          const endDist = Math.sqrt(Math.pow(seg.end.x - cornerX, 2) + Math.pow(seg.end.z - cornerZ, 2));
          
          if (startDist < snapRadius) {
            seg.start.x = cornerX;
            seg.start.z = cornerZ;
            console.log(`[Native AR Conversion] Snapped segment start to corner (confidence: ${confidence.toFixed(2)})`);
          }
          
          if (endDist < snapRadius) {
            seg.end.x = cornerX;
            seg.end.z = cornerZ;
            console.log(`[Native AR Conversion] Snapped segment end to corner (confidence: ${confidence.toFixed(2)})`);
          }
        });
      });
      
      return snappedSegments;
    };
    
    // Step 2.5: Snap nearby endpoints together for better connectivity
    let snappedWallSegments = snapWallEndpoints(validWallSegments);
    console.log(`[Native AR Conversion] After snapping: ${snappedWallSegments.length} wall segments`);
    
    // Step 2.7: Use corner data for enhanced snapping if available (Phase 2)
    if (corners.length > 0) {
      snappedWallSegments = applyCornerSnapping(snappedWallSegments, corners);
      console.log(`[Native AR Conversion] After corner-based snapping: ${snappedWallSegments.length} segments`);
    }
    
    // Step 3: Cluster and merge nearby wall endpoints
    const clusterWallEndpoints = (segments: typeof validWallSegments) => {
      const allPoints = segments.flatMap(seg => [
        { ...seg.start, sources: ['start'] },
        { ...seg.end, sources: ['end'] }
      ]);
      
      const clusteredPoints: Array<{ x: number; z: number; sources: string[]; count: number }> = [];
      const clusterThreshold = 0.15; // 15cm clustering threshold
      
      allPoints.forEach(point => {
        const existingCluster = clusteredPoints.find(cluster => 
          Math.sqrt(Math.pow(cluster.x - point.x, 2) + Math.pow(cluster.z - point.z, 2)) < clusterThreshold
        );
        
        if (existingCluster) {
          // Add to existing cluster (weighted average)
          const totalCount = existingCluster.count + 1;
          existingCluster.x = (existingCluster.x * existingCluster.count + point.x) / totalCount;
          existingCluster.z = (existingCluster.z * existingCluster.count + point.z) / totalCount;
          existingCluster.sources.push(...point.sources);
          existingCluster.count = totalCount;
        } else {
          clusteredPoints.push({
            x: point.x,
            z: point.z,
            sources: [...point.sources],
            count: 1
          });
        }
      });
      
      console.log(`[Native AR Conversion] Clustered ${allPoints.length} points into ${clusteredPoints.length} groups`);
      return clusteredPoints;
    };
    
    // Step 4: Find room bounds with outlier filtering
    const findRoomBounds = (segments: typeof validWallSegments) => {
      const allPoints = segments.flatMap(seg => [seg.start, seg.end]);
      
      // Calculate center of mass
      const centerX = allPoints.reduce((sum, p) => sum + p.x, 0) / allPoints.length;
      const centerZ = allPoints.reduce((sum, p) => sum + p.z, 0) / allPoints.length;
      
      // Filter out outliers (points more than 2 standard deviations from center)
      const distances = allPoints.map(p => Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.z - centerZ, 2)));
      const avgDistance = distances.reduce((sum, d) => sum + d, 0) / distances.length;
      const stdDev = Math.sqrt(distances.reduce((sum, d) => sum + Math.pow(d - avgDistance, 2), 0) / distances.length);
      const maxDistance = avgDistance + (2 * stdDev);
      
      const filteredPoints = allPoints.filter(p => {
        const distance = Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.z - centerZ, 2));
        return distance <= maxDistance;
      });
      
      console.log(`[Native AR Conversion] Filtered out ${allPoints.length - filteredPoints.length} outlier points`);
      
      if (filteredPoints.length === 0) return null;
      
      const minX = Math.min(...filteredPoints.map(p => p.x));
      const maxX = Math.max(...filteredPoints.map(p => p.x));
      const minZ = Math.min(...filteredPoints.map(p => p.z));
      const maxZ = Math.max(...filteredPoints.map(p => p.z));
      
      return {
        minX, maxX, minZ, maxZ,
        width: maxX - minX,
        length: maxZ - minZ,
        centerX: (minX + maxX) / 2,
        centerZ: (minZ + maxZ) / 2,
        filteredPoints
      };
    };

    const roomBounds = findRoomBounds(snappedWallSegments);
    if (!roomBounds) {
      console.log('[Native AR Conversion] Could not determine room bounds');
      return [];
    }
    
    console.log(`[Native AR Conversion] Room bounds: ${roomBounds.width.toFixed(2)}m × ${roomBounds.length.toFixed(2)}m`);
    console.log(`[Native AR Conversion] Room center: (${roomBounds.centerX.toFixed(2)}, ${roomBounds.centerZ.toFixed(2)})`);

    // Step 5: Use standard scale for consistency with manually drawn rooms
    const canvasSize = 3000;
    const canvasCenterX = canvasSize / 2;
    const canvasCenterY = canvasSize / 2;
    
    // Always use PIXELS_PER_METER to ensure consistency with the rest of the app
    // This ensures wall measurements and area calculations are accurate
    const scale = PIXELS_PER_METER;
    
    console.log(`[Native AR Conversion] Using standard scale: ${scale} pixels/meter for consistency`);

    // Step 6: Convert 3D coordinates to 2D canvas coordinates
    const convert3DTo2D = (point3D: { x: number; z: number }) => ({
      x: Math.round(canvasCenterX + (point3D.x - roomBounds.centerX) * scale),
      y: Math.round(canvasCenterY + (point3D.z - roomBounds.centerZ) * scale)
    });

    // Step 7: Create accurate room walls using actual wall segments
    const createWallsFromSegments = (): Wall[] => {
      const clusteredPoints = clusterWallEndpoints(snappedWallSegments);
      
      // Convert clustered points to 2D
      const points2D = clusteredPoints.map(cluster => ({
        ...convert3DTo2D({ x: cluster.x, z: cluster.z }),
        cluster: cluster,
        weight: cluster.count
      }));
      
      // Create walls by connecting wall segments intelligently
      const walls: Wall[] = [];
      const connectionThreshold = 30; // pixels
      
      snappedWallSegments.forEach((segment, index) => {
        const start2D = convert3DTo2D(segment.start);
        const end2D = convert3DTo2D(segment.end);
        
        // Find best matching clustered points for start and end
        const bestStart = points2D.reduce((best, point) => {
          const distance = Math.sqrt(Math.pow(point.x - start2D.x, 2) + Math.pow(point.y - start2D.y, 2));
          return (distance < connectionThreshold && (!best || distance < best.distance)) 
            ? { point, distance } 
            : best;
        }, null as { point: typeof points2D[0]; distance: number } | null);
        
        const bestEnd = points2D.reduce((best, point) => {
          const distance = Math.sqrt(Math.pow(point.x - end2D.x, 2) + Math.pow(point.y - end2D.y, 2));
          return (distance < connectionThreshold && (!best || distance < best.distance)) 
            ? { point, distance } 
            : best;
        }, null as { point: typeof points2D[0]; distance: number } | null);
        
        const finalStart = bestStart ? bestStart.point : start2D;
        const finalEnd = bestEnd ? bestEnd.point : end2D;
        
        // Verify the wall length makes sense
        const wallLength2D = Math.sqrt(Math.pow(finalEnd.x - finalStart.x, 2) + Math.pow(finalEnd.y - finalStart.y, 2));
        const realLength = wallLength2D / scale;
        
        if (realLength >= 0.3 && realLength <= 20) { // Reasonable wall length
          walls.push({
            id: `ar-wall-${index}-${uuidv4()}`,
            start: { x: finalStart.x, y: finalStart.y },
            end: { x: finalEnd.x, y: finalEnd.y },
            type: 'None',
            thickness: 10,
            color: '#000000',
            floor: currentFloor,
            external: true,
            isTemporary: false,
            arConfidence: segment.confidence // Store AR confidence for wall alignment
          } as Wall);
          
          console.log(`[Native AR Conversion] Wall ${index}: ${realLength.toFixed(2)}m (confidence: ${segment.confidence.toFixed(2)})`);
        }
      });
      
      return walls;
    };

    // Step 8: Create rectangular room as fallback
    const createRectangularRoom = (): Wall[] => {
      // Use filtered points for more accurate bounds
      const minCanvasX = Math.min(...roomBounds.filteredPoints.map(p => convert3DTo2D(p).x));
      const maxCanvasX = Math.max(...roomBounds.filteredPoints.map(p => convert3DTo2D(p).x));
      const minCanvasY = Math.min(...roomBounds.filteredPoints.map(p => convert3DTo2D(p).y));
      const maxCanvasY = Math.max(...roomBounds.filteredPoints.map(p => convert3DTo2D(p).y));
      
      // Minimal padding to stay accurate
      const padding = 10;
      const roomCorners = [
        { x: minCanvasX - padding, y: minCanvasY - padding },
        { x: maxCanvasX + padding, y: minCanvasY - padding },
        { x: maxCanvasX + padding, y: maxCanvasY + padding },
        { x: minCanvasX - padding, y: maxCanvasY + padding }
      ];
      
      const roomWalls: Wall[] = [];
      for (let i = 0; i < roomCorners.length; i++) {
        const start = roomCorners[i];
        const end = roomCorners[(i + 1) % roomCorners.length];
        
        roomWalls.push({
          id: `room-boundary-${i}-${uuidv4()}`,
          start: start,
          end: end,
          type: 'None',
          thickness: 10,
          color: '#000000',
          floor: currentFloor,
          external: true,
          isTemporary: false
        });
      }
      
      return roomWalls;
    };

    // Step 9: Try both approaches and choose the best one
    const segmentWalls = createWallsFromSegments();
    const rectangularRoom = createRectangularRoom();
    
    let finalWalls: Wall[];
    let approach: string;
    
    // Phase 2: Boost score for segment walls if we have high connectivity
    const connectivityBoost = connectivity.isClosedRoom ? 100 : (connectivity.connectivityRatio || 0) * 50;
    
    // Improved decision logic - prefer actual wall segments when available
    const evaluateWallAccuracy = (walls: Wall[], approachName: string) => {
      if (walls.length === 0) return { score: 0, reason: 'No walls' };
      
      const totalWalls = walls.length;
      const wallLengths = walls.map(wall => 
        Math.sqrt(Math.pow(wall.end.x - wall.start.x, 2) + Math.pow(wall.end.y - wall.start.y, 2)) / scale
      );
      
      const totalPerimeter = wallLengths.reduce((sum, length) => sum + length, 0);
      const averageWallLength = totalPerimeter / totalWalls;
      const minWallLength = Math.min(...wallLengths);
      
      let score = 0;
      let reasons = [];
      
      // Strong preference for actual wall segments from AR
      if (approachName === 'wall segments') {
        score += 150; // Highest priority for actual measured walls
        score += connectivityBoost; // Phase 2: Bonus for good connectivity
        reasons.push('uses actual AR wall data');
        
        if (connectivity.isClosedRoom) {
          reasons.push('closed room detected');
        }
        
        // Bonus for reasonable number of walls
        if (totalWalls >= 3 && totalWalls <= 12) {
          score += 50;
          reasons.push('reasonable wall count');
        }
        
        // Bonus for consistent wall lengths
        const lengthVariation = Math.max(...wallLengths) - Math.min(...wallLengths);
        if (lengthVariation < averageWallLength * 2) {
          score += 30;
          reasons.push('consistent wall lengths');
        }
      }
      
      if (approachName === 'rectangular') {
        score += 80; // Good fallback option
        reasons.push('clean rectangle');
        
        if (totalWalls === 4) {
          score += 20;
          reasons.push('proper rectangle');
        }
      }
      
      // Bonus for reasonable wall lengths
      if (minWallLength >= 0.5 && averageWallLength <= 15) {
        score += 25;
        reasons.push('realistic dimensions');
      }
      
      // Bonus for reasonable total area
      const estimatedArea = totalPerimeter * totalPerimeter / (4 * Math.PI); // Rough area estimate
      if (estimatedArea >= 5 && estimatedArea <= 200) { // 5-200 m²
        score += 20;
        reasons.push('realistic room size');
      }
      
      return { 
        score, 
        reason: reasons.join(', '),
        metrics: {
          wallCount: totalWalls,
          averageLength: averageWallLength.toFixed(2),
          perimeter: totalPerimeter.toFixed(2),
          estimatedArea: estimatedArea.toFixed(2)
        }
      };
    };
    
    // Evaluate both approaches
    const segmentEval = evaluateWallAccuracy(segmentWalls, 'wall segments');
    const rectangularEval = evaluateWallAccuracy(rectangularRoom, 'rectangular');
    
    console.log(`[Native AR Conversion] Wall segments quality: score=${segmentEval.score}, ${segmentEval.reason}`, segmentEval.metrics);
    console.log(`[Native AR Conversion] Rectangular room quality: score=${rectangularEval.score}, ${rectangularEval.reason}`, rectangularEval.metrics);
    
    // Choose the best approach
    if (segmentEval.score >= rectangularEval.score && segmentWalls.length > 0) {
      finalWalls = segmentWalls;
      approach = 'wall segments';
      console.log(`[Native AR Conversion] Chosen: wall segments (score: ${segmentEval.score} vs ${rectangularEval.score})`);
    } else if (rectangularRoom.length > 0) {
      finalWalls = rectangularRoom;
      approach = 'rectangular boundary';
      console.log(`[Native AR Conversion] Chosen: rectangular (score: ${rectangularEval.score} vs ${segmentEval.score})`);
    } else {
      console.log('[Native AR Conversion] No valid approach found, using empty result');
      finalWalls = [];
      approach = 'none';
    }
    
    // Add calibration information to the scan result
    const calibrationInfo = {
      scale: scale,
      standardScale: PIXELS_PER_METER,
      roomDimensions: {
        width: roomBounds.width,
        length: roomBounds.length,
        estimatedArea: roomBounds.width * roomBounds.length
      },
      wallCount: finalWalls.length,
      approach: approach,
      accuracy: segmentEval.score > rectangularEval.score ? 'high' : 'medium',
      phase2Enhancements: {
        cornersDetected: corners.length,
        isClosedRoom: connectivity.isClosedRoom || false,
        connectivityRatio: connectivity.connectivityRatio || 0,
        connectedWalls: connectivity.connectedWallCount || 0
      },
      phase3Enhancements: {
        overallQuality: qualityMetrics.overallQuality || 0,
        qualityLevel: qualityMetrics.qualityLevel || 'unknown',
        wallStability: qualityMetrics.wallStability || 0,
        coverageScore: qualityMetrics.coverageScore || 0,
        highConfidenceWalls: qualityMetrics.highConfidenceWalls || 0,
        frameCount: qualityMetrics.frameCount || 0,
        trackedWalls: qualityMetrics.trackedWalls || 0
      }
    };
    
    console.log(`[Native AR Conversion] Calibration info:`, calibrationInfo);
    console.log(`[Native AR Conversion] Using ${approach} approach: ${finalWalls.length} walls created`);
    
    if (finalWalls.length > 0) {
      console.log('[Native AR Conversion] Final wall measurements:', finalWalls.map((w, i) => ({
        index: i,
        start: w.start,
        end: w.end,
        length: `${((Math.sqrt(Math.pow(w.end.x - w.start.x, 2) + Math.pow(w.end.y - w.start.y, 2)) / scale)).toFixed(2)}m`
      })));
    }
    
    // Step 10: Conditionally align walls to canvas axes (straighten the room)
    if (finalWalls.length > 0 && shouldAlignWallsTo90Degrees) {
      console.log('[Native AR Conversion] 🔧 Aligning room to canvas axes (90° walls)...');
      finalWalls = alignWallsToAxes(finalWalls, scale, true); // Pass true for AR scan - minimal processing
      console.log('[Native AR Conversion] ✅ Room aligned to canvas axes');
      
      console.log(`[Native AR Conversion] Calibration info:`, calibrationInfo);
      console.log('[Native AR Conversion] Aligned wall measurements:', finalWalls.map((w, i) => ({
        index: i,
        start: { x: Math.round(w.start.x), y: Math.round(w.start.y) },
        end: { x: Math.round(w.end.x), y: Math.round(w.end.y) },
        length: `${((Math.sqrt(Math.pow(w.end.x - w.start.x, 2) + Math.pow(w.end.y - w.start.y, 2)) / scale)).toFixed(2)}m`
      })));
    } else if (finalWalls.length > 0) {
      console.log('[Native AR Conversion] ℹ️ Keeping original wall angles (non-standard angles mode)');
      
      // Log aligned measurements
      console.log('[Native AR Conversion] Aligned wall measurements:', finalWalls.map((w, i) => ({
        index: i,
        start: { x: Math.round(w.start.x), y: Math.round(w.start.y) },
        end: { x: Math.round(w.end.x), y: Math.round(w.end.y) },
        length: `${((Math.sqrt(Math.pow(w.end.x - w.start.x, 2) + Math.pow(w.end.y - w.start.y, 2)) / scale)).toFixed(2)}m`
      })));
      
      // Step 11: Calculate area using the same method as manually drawn rooms
      // Now that we use standard PIXELS_PER_METER scale, calculateArea() will work correctly
      try {
        const calculatedArea = calculateArea(finalWalls);
        console.log('[Native AR Conversion] 📐 AREA CALCULATION:', {
          areaFromShoelace: `${calculatedArea.toFixed(2)}m²`,
          wallCount: finalWalls.length,
          scaleUsed: `${scale} pixels/meter`,
          method: 'Same as manual room creation (bullet-proof)'
        });
      } catch (error) {
        console.error('[Native AR Conversion] ❌ Area calculation failed:', error);
      }
    }
    
    return finalWalls;
  }, [currentFloor]);

  // Convert manual scan with estimated coordinates
  const convertManualScanToFloorPlan = useCallback((scan: ARRoomScan): Wall[] => {
    console.log('[Manual AR Conversion] Converting manual scan:', {
      scanId: scan.id,
      wallsCount: scan.walls.length,
      walls: scan.walls
    });
    
    let walls: Wall[] = [];
    
    // Improved scaling system for manual scans
    const metersToPixels = 100; // 100 pixels per meter (better than 50)
    const canvasCenterX = 1500; // CANVAS_SIZE / 2
    const canvasCenterY = 1500; // CANVAS_SIZE / 2
    
    // Filter and process walls
    const validWalls = scan.walls.filter(arWall => {
      // Extract coordinates using multiple fallback methods
      let startX, startZ, endX, endZ;
      
      if (arWall.start && arWall.end) {
        // Method 1: Direct start/end properties (from RoomPlan native plugin)
        startX = arWall.start.x;
        startZ = arWall.start.z;
        endX = arWall.end.x;
        endZ = arWall.end.z;
      } else if (arWall.startPoint?.worldCoordinate && arWall.endPoint?.worldCoordinate) {
        // Method 2: Legacy worldCoordinate structure
        startX = arWall.startPoint.worldCoordinate.x;
        startZ = arWall.startPoint.worldCoordinate.z;
        endX = arWall.endPoint.worldCoordinate.x;
        endZ = arWall.endPoint.worldCoordinate.z;
      } else {
        return false; // Skip walls without valid coordinates
      }
      
      // Calculate wall length in meters
      const dx = endX - startX;
      const dy = endZ - startZ;
      const lengthMeters = Math.sqrt(dx * dx + dy * dy);
      
      // Filter out walls shorter than 10cm (likely noise)
      return lengthMeters >= 0.1;
    });

    console.log(`[Manual AR Conversion] Filtered ${scan.walls.length - validWalls.length} walls shorter than 10cm`);

    validWalls.forEach((arWall, index) => {
      // Extract 3D coordinates using multiple fallback methods
      let worldStartX, worldStartZ, worldEndX, worldEndZ;
      
      if (arWall.start && arWall.end) {
        // Method 1: Direct start/end properties (from RoomPlan native plugin)
        worldStartX = arWall.start.x;
        worldStartZ = arWall.start.z;
        worldEndX = arWall.end.x;
        worldEndZ = arWall.end.z;
      } else if (arWall.startPoint?.worldCoordinate && arWall.endPoint?.worldCoordinate) {
        // Method 2: Legacy worldCoordinate structure
        worldStartX = arWall.startPoint.worldCoordinate.x;
        worldStartZ = arWall.startPoint.worldCoordinate.z;
        worldEndX = arWall.endPoint.worldCoordinate.x;
        worldEndZ = arWall.endPoint.worldCoordinate.z;
      } else {
        console.error(`[Manual AR Conversion] Wall ${index}: No valid coordinates found`);
        return; // Skip this wall
      }
      
      // Convert world coordinates to canvas coordinates with improved scaling
      const startX = (worldStartX * metersToPixels) + canvasCenterX;
      const startY = (worldStartZ * metersToPixels) + canvasCenterY;
      const endX = (worldEndX * metersToPixels) + canvasCenterX;
      const endY = (worldEndZ * metersToPixels) + canvasCenterY;

      // Calculate actual wall length for verification
      const dx = endX - startX;
      const dy = endY - startY;
      const pixelLength = Math.sqrt(dx * dx + dy * dy);
      const calculatedMeters = pixelLength / metersToPixels;

      console.log(`[Manual AR Conversion] Wall ${index}:`, {
        originalWorldStart: { x: worldStartX, z: worldStartZ },
        originalWorldEnd: { x: worldEndX, z: worldEndZ },
        canvasStart: { x: Math.round(startX), y: Math.round(startY) },
        canvasEnd: { x: Math.round(endX), y: Math.round(endY) },
        calculatedLength: `${calculatedMeters.toFixed(2)}m`,
        originalLength: `${arWall.length?.toFixed(2)}m`
      });

      const wall: Wall = {
        start: { x: Math.round(startX), y: Math.round(startY) },
        end: { x: Math.round(endX), y: Math.round(endY) },
        type: 'None',
        floor: currentFloor,
        id: uuidv4()
      };

      walls.push(wall);
    });

    console.log('[Manual AR Conversion] Final converted walls:', {
      totalWalls: walls.length,
      walls: walls.map(w => ({
        start: w.start,
        end: w.end,
        length: `${(Math.sqrt(Math.pow(w.end.x - w.start.x, 2) + Math.pow(w.end.y - w.start.y, 2)) / metersToPixels).toFixed(2)}m`
      }))
    });
    
    // CRITICAL FIX: Apply wall alignment for manual scans too
    if (walls.length > 0 && shouldAlignWallsTo90Degrees) {
      console.log('[Manual AR Conversion] 🔧 Aligning walls to 90° axes...');
      walls = alignWallsToAxes(walls, metersToPixels);
      console.log('[Manual AR Conversion] ✅ Walls aligned to orthogonal axes');
    } else if (walls.length > 0) {
      console.log('[Manual AR Conversion] ℹ️ Keeping original wall angles (non-standard angles mode)');
    }
    
    return walls;
  }, [currentFloor, shouldAlignWallsTo90Degrees]);

  // Process and optimize scanned walls
  const optimizeScanData = useCallback((scan: ARRoomScan): ARRoomScan => {
    // Remove duplicate corners that are too close together
    const optimizedCorners = scan.corners.filter((corner, index, corners) => {
      return !corners.slice(0, index).some(existingCorner => {
        const distance = Math.sqrt(
          Math.pow(corner.worldCoordinate.x - existingCorner.worldCoordinate.x, 2) +
          Math.pow(corner.worldCoordinate.z - existingCorner.worldCoordinate.z, 2)
        );
        return distance < 0.1; // 10cm threshold
      });
    });

    // Snap corners to grid
    const snappedCorners = optimizedCorners.map(corner => ({
      ...corner,
      worldCoordinate: {
        ...corner.worldCoordinate,
        x: Math.round(corner.worldCoordinate.x * 20) / 20, // Snap to 5cm grid
        z: Math.round(corner.worldCoordinate.z * 20) / 20
      }
    }));

    // Recalculate walls with optimized corners
    const optimizedWalls = [];
    for (let i = 0; i < snappedCorners.length; i++) {
      const startCorner = snappedCorners[i];
      const endCorner = snappedCorners[(i + 1) % snappedCorners.length];
      
      const distance = Math.sqrt(
        Math.pow(endCorner.worldCoordinate.x - startCorner.worldCoordinate.x, 2) +
        Math.pow(endCorner.worldCoordinate.z - startCorner.worldCoordinate.z, 2)
      );

      // Only include walls longer than 20cm
      if (distance > 0.2) {
        optimizedWalls.push({
          id: uuidv4(),
          startPoint: startCorner,
          endPoint: endCorner,
          length: distance,
          height: scan.ceilingHeight,
          type: 'wall' as const,
          confidence: (startCorner.confidence + endCorner.confidence) / 2
        });
      }
    }

    return {
      ...scan,
      corners: snappedCorners,
      walls: optimizedWalls,
      floorArea: calculateFloorAreaFromCorners(snappedCorners),
      confidence: optimizedWalls.reduce((sum, wall) => sum + wall.confidence, 0) / optimizedWalls.length
    };
  }, []);

  // Calculate floor area from corners using shoelace formula
  const calculateFloorAreaFromCorners = useCallback((corners: ARRoomScan['corners']) => {
    if (!corners || corners.length < 3) return 0;

    let area = 0;
    for (let i = 0; i < corners.length; i++) {
      const j = (i + 1) % corners.length;
      area += corners[i].worldCoordinate.x * corners[j].worldCoordinate.z;
      area -= corners[j].worldCoordinate.x * corners[i].worldCoordinate.z;
    }
    return Math.abs(area) / 2;
  }, []);

  // Open AR scanner (manual)
  const openARScanner = useCallback(() => {
    setIsARScannerOpen(true);
  }, []);

  // Close AR scanner (manual)
  const closeARScanner = useCallback(() => {
    console.log('[AR Scanner] Closing manual AR scanner and resetting state');
    setIsARScannerOpen(false);
    setCurrentScan(null);
  }, []);

  // Open native AR scanner
  const openNativeARScanner = useCallback(() => {
    setIsNativeARScannerOpen(true);
  }, []);

  // Close native AR scanner
  const closeNativeARScanner = useCallback(() => {
    console.log('[AR Scanner] Closing native AR scanner and resetting state');
    setIsNativeARScannerOpen(false);
    setCurrentScan(null);
  }, []);

  // Handle scan completion
  const handleScanComplete = useCallback((scan: ARRoomScan) => {
    const optimizedScan = optimizeScanData(scan);
    setCurrentScan(optimizedScan);
    lastScanRef.current = optimizedScan;
    
    // Add to scanned rooms collection
    setScannedRooms(prev => [...prev, optimizedScan]);
  }, [optimizeScanData]);

  // Integrate scan into floor plan
  const integrateScanToFloorPlan = useCallback((scan: ARRoomScan) => {
    console.log('[AR Integration] Starting integration:', {
      scanId: scan.id,
      scanName: scan.name,
      scanData: scan
    });
    
    const walls = convertARToFloorPlan(scan);
    console.log('[AR Integration] Converted walls:', walls);
    
    onWallsGenerated(walls, scan);
    console.log('[AR Integration] Called onWallsGenerated');
    
    // NEW WORKFLOW: Close native scanner so orientation preview can show
    setIsARScannerOpen(false);
    setIsNativeARScannerOpen(false); // Close native scanner to show orientation
    setCurrentScan(null);
    console.log('[AR Integration] Integration complete - scanner closed, orientation preview will show');
  }, [convertARToFloorPlan, onWallsGenerated]);

  // Integrate multi-room scan results into floor plan
  const integrateMultiRoomScan = useCallback((multiRoomResult: any, setRoomCustomLabels?: (labels: Record<string, string>) => void) => {
    console.log('[Multi-Room Integration] Starting multi-room integration:', {
      roomCount: multiRoomResult.roomCount,
      rooms: multiRoomResult.rooms
    });

    // CRITICAL FIX: Collect ALL walls from ALL rooms in shared world coordinates
    // This preserves the spatial relationship between rooms
    const allWallsInWorldSpace: any[] = [];
    const allRooms: ARRoomScan[] = [];
    const roomLabels: Record<string, string> = {};
    const wallRoomMapping: Map<number, { index: number, roomId: string, roomName: string }> = new Map();

    multiRoomResult.rooms.forEach((roomData: any, index: number) => {
      console.log(`[Multi-Room Integration] Collecting walls from room ${index + 1}/${multiRoomResult.roomCount}`);

      // Store room metadata
      const roomScan: ARRoomScan = {
        id: roomData.roomId || uuidv4(),
        name: `Room ${index + 1}`,
        walls: roomData.walls || [],
        corners: roomData.corners || [],
        doors: roomData.doors || [],
        windows: roomData.windows || [],
        ceilingHeight: roomData.ceilingHeight || 2.4,
        floorArea: roomData.floorArea || 0,
        confidence: roomData.confidence || 0.8,
        scanDate: new Date(),
        scaleCalibration: 1.0
      };

      // Collect walls in world coordinates (don't convert yet)
      const roomWalls = roomData.walls || [];
      const wallStartIndex = allWallsInWorldSpace.length;
      
      allWallsInWorldSpace.push(...roomWalls);
      
      // Track which walls belong to which room
      for (let i = 0; i < roomWalls.length; i++) {
        wallRoomMapping.set(wallStartIndex + i, {
          index: index,
          roomId: roomData.roomId,
          roomName: `Room ${index + 1}`
        });
      }

      console.log(`[Multi-Room Integration] Room ${index + 1}: ${roomWalls.length} walls (world coords preserved)`);

      // Store room label for later application
      const roomIdentifier = `multiroom_${index}`;
      roomLabels[roomIdentifier] = `Room ${index + 1}`;
      
      console.log(`[Multi-Room Integration] Room ${index + 1} walls metadata:`, {
        roomId: roomIdentifier,
        wallCount: roomWalls.length,
        firstWallOwnership: (roomWalls[0] as any)?.originatingRoomId,
        allWallsHaveOwnership: roomWalls.every((w: any) => w.originatingRoomId === roomIdentifier)
      });

      allRooms.push(roomScan);
    });

    console.log(`[Multi-Room Integration] Collected ${allWallsInWorldSpace.length} walls from ${allRooms.length} rooms in shared world space`);

    // Create a combined scan with ALL walls in world coordinates
    const combinedWorldScan: ARRoomScan = {
      id: multiRoomResult.id,
      name: `Multi-Room Scan (${multiRoomResult.roomCount} rooms)`,
      walls: allWallsInWorldSpace,
      corners: [],
      doors: [],
      windows: [],
      ceilingHeight: allRooms.reduce((sum, r) => sum + r.ceilingHeight, 0) / allRooms.length,
      floorArea: allRooms.reduce((sum, r) => sum + r.floorArea, 0),
      confidence: allRooms.reduce((sum, r) => sum + r.confidence, 0) / allRooms.length,
      scanDate: new Date(),
      scaleCalibration: 1.0
    };

    // NOW convert ALL walls together - this preserves spatial relationships
    console.log('[Multi-Room Integration] Converting all walls together to preserve room connections...');
    const allConvertedWalls = convertNativeARKitToFloorPlan(combinedWorldScan);

    console.log(`[Multi-Room Integration] Converted ${allConvertedWalls.length} connected walls`);

    // Apply room metadata to converted walls
    const wallsWithMetadata = allConvertedWalls.map((wall, index) => {
      const roomInfo = wallRoomMapping.get(index);
      if (roomInfo) {
        return {
          ...wall,
          roomIndex: roomInfo.index,
          roomId: roomInfo.roomId,
          roomName: roomInfo.roomName,
          originatingRoomId: `multiroom_${roomInfo.index}`,
          assignedRoomIds: [`multiroom_${roomInfo.index}`]
        };
      }
      return wall;
    });

    // 🔧 CRITICAL FIX: Deduplicate shared walls between rooms
    // Only merge walls that are ACTUALLY shared (connecting walls), not just nearby walls
    console.log(`[Multi-Room Integration] Deduplicating shared walls...`);
    const deduplicatedWalls: any[] = [];
    const DUPLICATE_THRESHOLD = 3; // Tighter threshold - only truly overlapping walls
    
    wallsWithMetadata.forEach((wall, index) => {
      const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      
      // Check if this wall is a duplicate of an already processed wall
      const duplicateIndex = deduplicatedWalls.findIndex(existing => {
        // CRITICAL: Only merge if walls are from DIFFERENT rooms
        if (existing.originatingRoomId === wall.originatingRoomId) {
          return false; // Same room - never merge
        }
        
        // Check if start and end points match (in either direction)
        const startStartDist = Math.hypot(existing.start.x - wall.start.x, existing.start.y - wall.start.y);
        const endEndDist = Math.hypot(existing.end.x - wall.end.x, existing.end.y - wall.end.y);
        const startEndDist = Math.hypot(existing.start.x - wall.end.x, existing.start.y - wall.end.y);
        const endStartDist = Math.hypot(existing.end.x - wall.start.x, existing.end.y - wall.start.y);
        
        // Same direction or reversed direction
        const sameDirection = (startStartDist < DUPLICATE_THRESHOLD && endEndDist < DUPLICATE_THRESHOLD);
        const reversedDirection = (startEndDist < DUPLICATE_THRESHOLD && endStartDist < DUPLICATE_THRESHOLD);
        
        // Additional check: walls should have similar lengths (within 10%)
        const existingLength = Math.hypot(existing.end.x - existing.start.x, existing.end.y - existing.start.y);
        const lengthRatio = wallLength / existingLength;
        const similarLength = lengthRatio > 0.9 && lengthRatio < 1.1;
        
        return (sameDirection || reversedDirection) && similarLength;
      });
      
      if (duplicateIndex !== -1) {
        // This is a duplicate - merge room assignments
        const existing = deduplicatedWalls[duplicateIndex];
        const newRoomId = wall.originatingRoomId;
        
        if (!existing.assignedRoomIds.includes(newRoomId)) {
          existing.assignedRoomIds.push(newRoomId);
        }
        
        const wallLengthMeters = (wallLength / 100).toFixed(2);
        console.log(`[Multi-Room Integration] 🔗 Merged shared wall (${wallLengthMeters}m): ${existing.originatingRoomId} + ${newRoomId} -> ${existing.assignedRoomIds.join(', ')}`);
      } else {
        // Not a duplicate - add to list
        deduplicatedWalls.push(wall);
      }
    });

    console.log(`[Multi-Room Integration] Deduplication complete: ${wallsWithMetadata.length} → ${deduplicatedWalls.length} walls`);
    
    // Log wall distribution per room
    const wallCountsByRoom: Record<string, number> = {};
    deduplicatedWalls.forEach(wall => {
      wall.assignedRoomIds.forEach((roomId: string) => {
        wallCountsByRoom[roomId] = (wallCountsByRoom[roomId] || 0) + 1;
      });
    });
    console.log(`[Multi-Room Integration] Wall distribution:`, wallCountsByRoom);

    const allWalls = deduplicatedWalls;

    console.log('[Multi-Room Integration] All rooms processed:', {
      totalRooms: allRooms.length,
      totalWalls: allWalls.length
    });

    // Create a combined scan object for metadata
    const combinedScan: ARRoomScan & { roomLabels?: Record<string, string> } = {
      id: multiRoomResult.id,
      name: `Multi-Room Scan (${multiRoomResult.roomCount} rooms)`,
      walls: [],
      corners: [],
      doors: [],
      windows: [],
      ceilingHeight: allRooms.reduce((sum, r) => sum + r.ceilingHeight, 0) / allRooms.length,
      floorArea: allRooms.reduce((sum, r) => sum + r.floorArea, 0),
      confidence: allRooms.reduce((sum, r) => sum + r.confidence, 0) / allRooms.length,
      scanDate: new Date(),
      scaleCalibration: 1.0,
      roomLabels: roomLabels // Attach room labels to scan data
    };

    // Store all rooms
    setScannedRooms(allRooms);

    // Send ALL walls at once to the floor plan WITH room labels
    console.log('[Multi-Room Integration] Sending all walls to floor plan with labels:', {
      walls: allWalls.length,
      labels: Object.keys(roomLabels).length,
      roomLabels
    });
    onWallsGenerated(allWalls, combinedScan);

    // Close scanners
    setIsARScannerOpen(false);
    setIsNativeARScannerOpen(false);
    setCurrentScan(null);

    console.log('[Multi-Room Integration] Multi-room integration complete');
  }, [convertNativeARKitToFloorPlan, onWallsGenerated]);

  // Get scan by ID
  const getScanById = useCallback((scanId: string): ARRoomScan | undefined => {
    return scannedRooms.find(scan => scan.id === scanId);
  }, [scannedRooms]);

  // Delete scan
  const deleteScan = useCallback((scanId: string) => {
    setScannedRooms(prev => prev.filter(scan => scan.id !== scanId));
  }, []);

  // Get scan statistics
  const getScanStats = useCallback(() => {
    return {
      totalScans: scannedRooms.length,
      totalArea: scannedRooms.reduce((sum, scan) => sum + scan.floorArea, 0),
      averageConfidence: scannedRooms.length > 0 
        ? scannedRooms.reduce((sum, scan) => sum + scan.confidence, 0) / scannedRooms.length 
        : 0,
      lastScanDate: scannedRooms.length > 0 
        ? Math.max(...scannedRooms.map(scan => scan.scanDate.getTime()))
        : null
    };
  }, [scannedRooms]);

  // Export scan data
  const exportScanData = useCallback(() => {
    return {
      scans: scannedRooms,
      metadata: {
        exportDate: new Date(),
        totalScans: scannedRooms.length,
        version: '1.0'
      }
    };
  }, [scannedRooms]);

  // Import scan data
  const importScanData = useCallback((data: { scans: ARRoomScan[] }) => {
    const validScans = data.scans.filter(scan => 
      scan.id && scan.walls && scan.corners && scan.scanDate
    );
    setScannedRooms(validScans);
  }, []);

  return {
    // State
    scannedRooms,
    currentScan,
    isARScannerOpen,
    isNativeARScannerOpen,
    
    // Actions
    openARScanner,
    closeARScanner,
    openNativeARScanner,
    closeNativeARScanner,
    handleScanComplete,
    integrateScanToFloorPlan,
    integrateMultiRoomScan,
    deleteScan,
    
    // Utilities
    getScanById,
    getScanStats,
    exportScanData,
    importScanData,
    convertARToFloorPlan,
    optimizeScanData
  };
}; 